import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * EmptyState — a centered placeholder for "nothing here yet" / "no results" /
 * "no data" / error surfaces: an optional muted icon in a soft circle, a title,
 * an optional description, and an optional action slot (drop a <Button> in).
 *
 * One component covers the whole range the app hand-rolls today — a compact
 * in-card/in-list "No results" (`size="sm"`) through a full-panel onboarding
 * prompt with a CTA (`size="md"`). Reach for this instead of a bespoke
 * icon-over-text block; for an inline *colored* status notice use <Alert> with
 * the matching status instead.
 *
 *   size   sm | md   (sm for in-card/in-list, md for full panels/pages)
 *
 * Root is role="status" so assistive tech announces it when a list empties
 * (e.g. after a filter clears every row). The icon is decorative (aria-hidden).
 */

type EmptyStateSize = 'sm' | 'md'

const sizeStyles: Record<
  EmptyStateSize,
  { root: string; iconBox: string; group: string; title: string; body: string }
> = {
  sm: {
    root: 'gap-3 py-8',
    iconBox: 'size-10 [&>svg]:size-5',
    group: 'gap-0.5',
    title: 'text-sm',
    body: 'max-w-xs text-xs',
  },
  md: {
    root: 'gap-4 py-12',
    iconBox: 'size-14 [&>svg]:size-7',
    group: 'gap-1',
    title: 'text-base',
    body: 'max-w-sm text-sm',
  },
}

export interface EmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Decorative glyph (e.g. a Phosphor icon), shown in a soft circle above the title. */
  icon?: React.ReactNode
  /** Headline — the primary "nothing here" message. */
  title: React.ReactNode
  /** Optional supporting sentence beneath the title. */
  description?: React.ReactNode
  /** Optional action slot — typically a <Button> (or a couple of them). */
  action?: React.ReactNode
  size?: EmptyStateSize
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    { className, icon, title, description, action, size = 'md', ...props },
    ref
  ) => {
    const s = sizeStyles[size]
    return (
      <div
        ref={ref}
        role="status"
        data-slot="empty-state"
        className={cn(
          'flex flex-col items-center justify-center px-4 text-center',
          s.root,
          className
        )}
        {...props}
      >
        {icon && (
          <span
            data-slot="empty-state-icon"
            aria-hidden="true"
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full bg-fbui-gray-100 text-fbui-gray-400',
              s.iconBox
            )}
          >
            {icon}
          </span>
        )}
        <div className={cn('flex flex-col', s.group)}>
          <p
            data-slot="empty-state-title"
            className={cn(
              'font-semibold tracking-[-0.2px] text-fbui-gray-900',
              s.title
            )}
          >
            {title}
          </p>
          {description != null && (
            <p
              data-slot="empty-state-description"
              className={cn('tracking-[-0.2px] text-fbui-gray-500', s.body)}
            >
              {description}
            </p>
          )}
        </div>
        {action && <div data-slot="empty-state-action">{action}</div>}
      </div>
    )
  }
)
EmptyState.displayName = 'EmptyState'
